/**
 * Base de datos local (IndexedDB vía Dexie).
 *
 * El registro de entrenamientos es *local-first*: la sesión activa y sus series
 * viven aquí desde el primer instante, con o sin red, y el servidor se entera
 * después a través de la cola de `syncOps`. Todo se direcciona por el
 * `clientId` que genera el propio dispositivo, que es también la clave de
 * idempotencia del contrato (`PUT /v1/workouts/{clientId}`).
 *
 * Cada cuenta tiene su propia base (`fitness-<userId>`), que se abre al saber
 * quién ha entrado y se cierra al salir: una cuenta nunca ve ni sube lo que
 * otra guardó en el mismo navegador. Por eso no hay una instancia global; se
 * pide la de la cuenta activa con {@link getDb} (o {@link useLocalDb} en un
 * componente). La base compartida de antes (`fitness`) se reparte a su dueño
 * en `account.ts`.
 */
import type {
  ExerciseCategory,
  FoodUnit,
  MacroTotals,
  MealEntryStatus,
  MealType,
  RoutineSetTarget,
  SetType,
} from '@fitness/api-client';
import type { SyncOp } from '@fitness/core';
import Dexie, { type EntityTable } from 'dexie';
import { useSyncExternalStore } from 'react';

/**
 * Ejercicio prescrito para la sesión, copiado del día de la rutina en el
 * momento de empezar. Se guarda dentro de la sesión —y no se vuelve a pedir— para
 * que la pantalla de entreno se pinte entera sin conexión.
 */
export interface LocalSessionTarget {
  exerciseId: string;
  exerciseName: string;
  /** Primera imagen del ejercicio, o `null`. */
  exerciseMedia: string | null;
  /** Orden dentro del día, empezando en 0. */
  position: number;
  targetSets: number;
  targetRepsMin: number | null;
  targetRepsMax: number | null;
  targetRpe: number | null;
  restSeconds: number | null;
  notes: string | null;
  /*
   * Lo que sigue llegó después: las sesiones guardadas antes no lo tienen, y
   * por eso todo es opcional. Ausente equivale a `null` (o `false`).
   */
  /** Categoría del ejercicio; `cardio` registra duración y distancia. */
  exerciseCategory?: ExerciseCategory | null;
  /** Ejercicio unilateral: las repeticiones son por lado y el volumen cuenta doble. */
  unilateral?: boolean;
  /** Objetivo serie a serie (14-12-10-10); manda sobre el rango cuando existe. */
  targetSetsDetail?: RoutineSetTarget[] | null;
  targetRir?: number | null;
  targetDurationSeconds?: number | null;
  targetDistanceM?: number | null;
  targetHeartRate?: number | null;
}

/** Sesión de entrenamiento tal y como la guarda el dispositivo. */
export interface LocalSession {
  /** UUID generado aquí; viaja como `clientId` al servidor. */
  clientId: string;
  routineId: string | null;
  routineDayId: string | null;
  /** Copia del nombre para poder titular la sesión sin pedir la rutina. */
  routineName: string | null;
  dayName: string | null;
  startedAt: string;
  finishedAt: string | null;
  notes: string | null;
  /** Ejercicios previstos, en orden. Vacío en un entrenamiento libre. */
  targets: LocalSessionTarget[];
  /** Epoch ms en que el servidor la aceptó, o `null` si sigue pendiente. */
  syncedAt: number | null;
  /** Motivo por el que el servidor la rechazó; no se reintenta sola. */
  syncError: string | null;
}

/** Serie registrada en el dispositivo. */
export interface LocalSet {
  clientId: string;
  sessionClientId: string;
  exerciseId: string;
  exerciseName: string;
  exerciseMedia: string | null;
  setNumber: number;
  setType: SetType;
  reps: number;
  weightKg: number;
  rpe: number | null;
  performedAt: string;
  notes: string | null;
  syncedAt: number | null;
  syncError: string | null;
  /** Cardio: duración en segundos. Opcional: las series antiguas no lo tienen. */
  durationSeconds?: number | null;
  /** Cardio: distancia en metros. */
  distanceM?: number | null;
  /** Copia de `unilateral` del ejercicio, para el volumen sin conexión. */
  unilateral?: boolean;
  /** Copia de la categoría del ejercicio. */
  exerciseCategory?: ExerciseCategory | null;
}

/**
 * Entrada del diario de comidas tal y como la guarda el dispositivo.
 *
 * Los `totals` se calculan aquí para poder pintar la comida en el instante en
 * que se apunta; el servidor los recalcula y los congela al aceptarla, y a
 * partir de ese momento manda la copia del servidor.
 *
 * Sólo puede haber una de `foodId` / `recipeId`; con las dos a `null` la
 * entrada es manual y sus macros son exactamente las de `totals`.
 */
export interface LocalMealEntry {
  /** UUID generado aquí; viaja como `clientId` al servidor. */
  clientId: string;
  /** Día del diario, `YYYY-MM-DD`. */
  date: string;
  meal: MealType;
  foodId: string | null;
  recipeId: string | null;
  /** Nombre con el que se pinta; se copia al apuntar y no cambia después. */
  label: string;
  /** Gramos/ml con `foodId`, raciones con `recipeId`, unidades si es manual. */
  quantity: number;
  /** Unidad de `quantity` cuando sale de un alimento; `null` en el resto. */
  unit: FoodUnit | null;
  totals: MacroTotals;
  loggedAt: string;
  notes: string | null;
  /**
   * `planned` si es una comida planificada que aún no se ha comido (no cuenta
   * en los totales) y `eaten` si ya se comió. Las filas guardadas antes de que
   * existieran los planes no lo traen: sin `status` la entrada es `eaten`.
   */
  status?: MealEntryStatus;
  /** Epoch ms en que el servidor la aceptó, o `null` si sigue pendiente. */
  syncedAt: number | null;
  /** Motivo por el que el servidor la rechazó; no se reintenta sola. */
  syncError: string | null;
  /**
   * Lápida: instante en que se borró en el dispositivo. La fila se conserva
   * hasta que el servidor confirma el borrado, para que el diario no vuelva a
   * enseñar la entrada al fusionarse con la respuesta del servidor.
   */
  deletedAt: string | null;
}

/**
 * Esquema de la base local.
 *
 * Sólo se declaran los índices; Dexie guarda el objeto entero, así que campos
 * como `targets` o `syncError` viajan sin necesidad de indexarse. `syncedAt` no
 * se indexa a propósito: IndexedDB no indexa `null`, y justamente lo que se
 * busca son las filas que lo tienen a `null`.
 *
 * La versión 2 añade `mealEntries` sin tocar nada de la 1: Dexie aplica el
 * cambio de esquema solo, y una base que venía de entrenamiento se abre con sus
 * sesiones y series intactas.
 */
export class FitnessDB extends Dexie {
  declare sessions: EntityTable<LocalSession, 'clientId'>;
  declare sets: EntityTable<LocalSet, 'clientId'>;
  declare syncOps: EntityTable<SyncOp, 'id'>;
  declare mealEntries: EntityTable<LocalMealEntry, 'clientId'>;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      sessions: 'clientId, startedAt, routineId',
      sets: 'clientId, sessionClientId, exerciseId, performedAt',
      syncOps: 'id, createdAt',
    });
    this.version(2).stores({
      mealEntries: 'clientId, date, loggedAt',
    });
  }
}

/**
 * Nombre de la base que compartían todas las cuentas antes de separarlas. Ya no
 * se crea: sólo se lee para repartirla a su dueño (ver `account.ts`).
 */
export const LEGACY_DB_NAME = 'fitness';

/** Nombre de la base de una cuenta. */
export function userDbName(userId: string): string {
  return `${LEGACY_DB_NAME}-${userId}`;
}

// --- Base de la cuenta activa ---------------------------------------------

/** Se pide la base local sin ninguna cuenta dentro. */
export class NoLocalDbError extends Error {
  constructor() {
    super('No hay ninguna cuenta con sesión: la base local está cerrada.');
    this.name = 'NoLocalDbError';
  }
}

let current: { userId: string; db: FitnessDB } | null = null;
const dbListeners = new Set<() => void>();

function notifyDbChange(): void {
  for (const listener of dbListeners) listener();
}

/**
 * Abre la base de una cuenta y la deja como activa. Si había otra abierta, la
 * cierra antes: nunca hay dos activas a la vez.
 */
export function openUserDb(userId: string): FitnessDB {
  if (current?.userId === userId) return current.db;
  closeUserDb();
  const db = new FitnessDB(userDbName(userId));
  current = { userId, db };
  notifyDbChange();
  return db;
}

/** Cierra la base de la cuenta activa. Lo guardado se queda en el navegador. */
export function closeUserDb(): void {
  if (current === null) return;
  current.db.close();
  current = null;
  notifyDbChange();
}

/** La base de la cuenta activa. Lanza {@link NoLocalDbError} si no hay ninguna. */
export function getDb(): FitnessDB {
  if (current === null) throw new NoLocalDbError();
  return current.db;
}

/** La base de la cuenta activa, o `null`. */
export function currentDb(): FitnessDB | null {
  return current?.db ?? null;
}

/** Cuenta dueña de la base abierta, o `null`. */
export function localDbUserId(): string | null {
  return current?.userId ?? null;
}

function subscribeLocalDb(listener: () => void): () => void {
  dbListeners.add(listener);
  return () => {
    dbListeners.delete(listener);
  };
}

/**
 * La base de la cuenta activa para un componente, o `null` mientras no hay
 * ninguna (justo al cerrar sesión, antes de llegar a `/login`). Las consultas
 * en vivo la ponen en sus dependencias para volver a suscribirse al cambiar
 * de cuenta.
 */
export function useLocalDb(): FitnessDB | null {
  return useSyncExternalStore(subscribeLocalDb, currentDb, currentDb);
}

/** Identificador de cliente nuevo. */
export function newClientId(): string {
  return crypto.randomUUID();
}

/** La sesión abierta (sin `finishedAt`), la más reciente si hubiera varias. */
export async function activeSession(): Promise<LocalSession | undefined> {
  const open = await getDb()
    .sessions.filter((session) => session.finishedAt === null)
    .toArray();
  open.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  return open[0];
}

/** Series de una sesión en el orden en que se hicieron. */
export async function sessionSets(sessionClientId: string): Promise<LocalSet[]> {
  const rows = await getDb().sets.where('sessionClientId').equals(sessionClientId).toArray();
  return rows.sort((a, b) => a.performedAt.localeCompare(b.performedAt));
}

/** Entradas del diario guardadas para un día, lápidas incluidas. */
export async function mealEntriesOfDay(date: string): Promise<LocalMealEntry[]> {
  const rows = await getDb().mealEntries.where('date').equals(date).toArray();
  return rows.sort((a, b) => a.loggedAt.localeCompare(b.loggedAt));
}
